import { BillingEntityFormData, emptyBillingEntityFormData } from '@domains/account/state/state';

/**
 * Onboarding domain state.
 *
 * Holds only the persisted-bound data for the first-entry onboarding wizard
 * (invoicing integration + billing entity) and the ephemeral submit status.
 * The wizard step index is NOT here — it is ephemeral UI state owned by
 * `useWizard` (see `components/wizard/`).
 *
 * The billing form shape is reused verbatim from the `account` domain
 * (`BillingEntityFormData`) so the field set never drifts between the
 * onboarding wizard and the canonical billing screen.
 */
export interface OnboardingState {
  /** Account onboarding flag, synced from AccountsReadmodel (source: api). */
  data: {
    onboardingCompletedAt: string | null;
    accountId: string | null;
  };

  /** Wizard lifecycle UI state. */
  ui: {
    /** Re-run on demand: the wizard is shown even when onboarding is already complete. */
    manuallyOpened: boolean;
  };

  /** Invoicing step state. */
  invoicing: {
    selectedConnectorId: string | null;
    credentials: Record<string, string>;
    /** Set once an invoicing integration has been provisioned successfully. */
    provisionedIntegrationId: string | null;
  };

  /** Billing step state — reuses the account-domain billing form shape. */
  billing: {
    form: BillingEntityFormData;
    errors: Record<string, string>;
  };

  /** Loading states, one per async operation. */
  loading: {
    submittingInvoicing: boolean;
    submittingBilling: boolean;
    completing: boolean;
  };

  /** Top-level errors. */
  errors: Record<string, string>;
}

/**
 * Initial onboarding state.
 */
export const initialState: OnboardingState = {
  data: {
    onboardingCompletedAt: null,
    accountId: null
  },

  ui: {
    manuallyOpened: false
  },

  invoicing: {
    selectedConnectorId: null,
    credentials: {},
    provisionedIntegrationId: null
  },

  billing: {
    form: emptyBillingEntityFormData,
    errors: {}
  },

  loading: {
    submittingInvoicing: false,
    submittingBilling: false,
    completing: false
  },

  errors: {}
};

/**
 * Derived state selectors.
 */
export const selectors = {
  /** First-entry detection: onboarding is required until the account flag is set. */
  selectIsOnboardingRequired: ( state: OnboardingState ): boolean => {
    return !state.data.onboardingCompletedAt;
  },

  /** Whether the user re-opened the wizard on demand after completion or dismissal. */
  selectManuallyOpened: ( state: OnboardingState ): boolean =>
    state.ui.manuallyOpened,

  /** Invoicing step can finish once an integration has been provisioned. */
  selectCanFinishInvoicing: ( state: OnboardingState ): boolean => {
    return state.invoicing.provisionedIntegrationId != null;
  },

  /**
   * Billing step can finish when the billing form is valid. Replicates the
   * account-domain `isBillingFormValid` predicate: all required fields
   * non-empty, including `invoicingIntegrationId`.
   */
  selectCanFinishBilling: ( state: OnboardingState ): boolean => {
    const { form } = state.billing;
    return (
      form.name.trim().length > 0 &&
      form.legalName.trim().length > 0 &&
      form.documentId.trim().length > 0 &&
      form.billingAddress.trim().length > 0 &&
      form.invoicingIntegrationId.trim().length > 0
    );
  }
};
